import { useLingui } from '@lingui/react/macro';
import { ArrowLeft2, Wallet2 } from 'iconsax-reactjs';
import {
  type FC,
  type ReactNode,
  type RefObject,
  useEffect,
  useRef,
} from 'react';
import { createAuthNextPath, useHasAccessToken } from '@/auth/api';
import { WORKSPACE } from '@/base/constants';
import { Link, useLocation } from '@/base/router';
import { DevToolsButton } from '@/dev-tools/DevToolsButton';
import { Gate } from '@/gate';
import { LanguageSelect } from '@/settings/LanguageSelect';
import { ThemeSelect } from '@/settings/ThemeSelect';
import { Button } from '@/shared/Button';
import { debounce } from '@/shared/js';
import { Logo } from '@/shared/Logo';
import { cn } from '@/shared/react';
import { useMe } from '../../../account/profile/api';
import { HeaderNavLink } from './HeaderNavLink';
import { ProfileMenu } from './ProfileMenu';
import { useHeaderLinks } from './useHeaderLinks';

export type HeaderPosition = 'smart-sticky' | 'sticky' | 'relative';

export const useHeaderPosition = (
  ref: RefObject<HTMLElement | null>,
  position: HeaderPosition,
) => {
  useEffect(() => {
    if (position !== 'smart-sticky') return;
    const el = ref.current;
    if (!el) return;
    let prev = 0;
    const handler = debounce(() => {
      const current = window.scrollY || 0;
      if (current > 56) {
        el.setAttribute('data-passed', 'true');
      } else {
        el.removeAttribute('data-passed');
      }
      if (current < prev) {
        el.setAttribute('data-stick', 'true');
      } else {
        el.removeAttribute('data-stick');
      }
      prev = current;
    }, 10);
    window.addEventListener('scroll', handler.run);

    return () => {
      window.removeEventListener('scroll', handler.run);
      el.removeAttribute('data-stick');
      el.removeAttribute('data-passed');
      handler.clear();
    };
  }, [position, ref]);
};

export const Header: FC<{
  className?: string;
  position: HeaderPosition;
  title?: ReactNode;
  backLink?: string;
  actions?: ReactNode;
}> = ({ className, position, title, backLink, actions }) => {
  const ref = useRef<HTMLElement>(null);
  const { t } = useLingui();
  const hasAccessToken = useHasAccessToken();
  const links = useHeaderLinks();
  const [location] = useLocation();
  const me = useMe();

  useHeaderPosition(ref, position);

  return (
    <nav
      className={cn(
        'border-b border-border-disabled bg-background-primary h-14 transition-all duration-300',
        position !== 'relative' && 'sticky top-0 z-10',
        'data-passed:-translate-y-full data-passed:data-stick:translate-y-0',
        me.isLoading && 'pointer-events-none',
        className,
      )}
      ref={ref}
    >
      <div className="flex items-center px-3 gap-3 max-w-desktop mx-auto h-full">
        {backLink && (
          <Link to={backLink ?? '/'} className="contents">
            <Button variant="outline" fab size={8} className="desktop:hidden">
              <ArrowLeft2 className="auto-rtl" size={20} />
            </Button>
          </Link>
        )}
        <Link
          to="/"
          workspace="public"
          className={cn(title && 'hidden desktop:flex')}
        >
          <Logo text className="w-16" />
        </Link>
        <span className="hidden desktop:block bg-border-disabled h-6 w-px shrink-0" />
        <div className="hidden desktop:contents">
          {links
            .filter((x) => x.level === 1 && !x.hidden)
            .map((item) => (
              <HeaderNavLink key={item.key} item={item} mode="desktop" />
            ))}
        </div>
        <div className="grow overflow-hidden">{title}</div>

        {hasAccessToken && (
          <div className="hidden desktop:contents">
            <Gate hooks={[Gate.hooks.hasFeature('deposit-iqd')]}>
              <Link workspace="app" to="/deposit?type=iqd" className="contents">
                <Button variant="primary" size={8}>
                  {t`Deposit IQD`}
                </Button>
              </Link>
            </Gate>
            <Link workspace="app" to="/wallet" className="contents">
              <Button variant="outline" size={8}>
                <Wallet2 size={16} /> {t`Wallet`}
              </Button>
            </Link>
            <span className="bg-border-disabled h-6 w-px shrink-0" />
          </div>
        )}
        <div className="hidden desktop:contents">
          <LanguageSelect mini size={8} arrow={false} className="w-auto px-2" />
          <ThemeSelect size={8} arrow={false} className="px-0 !w-8" mini />
        </div>
        <DevToolsButton />
        {!hasAccessToken && (
          <Link
            workspace="app"
            to={`/auth?next=${createAuthNextPath(WORKSPACE, location)}`}
            className="shrink-0"
          >
            <Button size={8} variant="primary" className="min-w-28">
              {t`Login / Sign Up`}
            </Button>
          </Link>
        )}
        <ProfileMenu />

        {actions}
      </div>
    </nav>
  );
};
